/**
 * @description Dialog component for capturing and editing medical prescription details (prescriber ID, specialty, type, RPPS, date).
 * @usageNotes Opened as a MatDialog from BsDialogComponent upon clicking the Prescription button.
 * Depends on FactureFR and Prescription DAO models, and utils.
 */

import { Component, inject, OnInit, AfterViewInit, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';


import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Prescription, FactureFR } from '../shared/dao';
import * as utils from '../shared/utils';

/**
 * Data payload passed into PrescriptionComponent dialog.
 */
export interface DialogData {
  server_url: string;
  order_id: number;
  factureFR: FactureFR;
  isDisabled: boolean;
  previousPrescribers?: string[];
}

@Component({
  selector: 'lib-prescription',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatButtonModule, MatButtonToggleModule, MatTooltipModule, MatDialogContent, MatDialogActions],
  templateUrl: './prescription.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prescription.component.scss', '../bs-dialog/bs-dialog.component.scss'],
})

export class PrescriptionComponent implements OnInit, AfterViewInit {
  readonly dialogRef = inject(MatDialogRef<PrescriptionComponent>);
  readonly dialogdata: DialogData = inject<DialogData>(MAT_DIALOG_DATA);

  @ViewChild('datePrescInput') datePrescInput?: ElementRef<HTMLInputElement>;

  /** Current prescription model being edited */
  public prescription: Prescription;

  /** List of previous prescriber IDs for this patient to offer as suggestions */
  public previousPrescribers: string[] = [];

  constructor() {
    this.prescription = Prescription.createDefault();
  }

  /**
   * Initializes component state with prescription values from the invoice header,
   * defaulting datePresc to today / dateFacture and typePresc to 'L' (Libéral) if not set.
   */
  ngOnInit(): void {
    this.previousPrescribers = this.dialogdata.previousPrescribers ?? [];
    this.prescription.numIdentPresc = this.dialogdata.factureFR.prescription.numIdentPresc;

    this.prescription.codeSpecPresc = this.dialogdata.factureFR.prescription.codeSpecPresc === '' ? '01' : this.dialogdata.factureFR.prescription.codeSpecPresc;
    this.prescription.RPPSPresc = this.dialogdata.factureFR.prescription.RPPSPresc === '' ? '' : this.dialogdata.factureFR.prescription.RPPSPresc;
    this.prescription.numStructPresc = this.dialogdata.factureFR.prescription.numStructPresc === '' ? '' : this.dialogdata.factureFR.prescription.numStructPresc;
    this.prescription.typePresc = this.dialogdata.factureFR.prescription.typePresc === '' ? 'L' : this.dialogdata.factureFR.prescription.typePresc;
    this.prescription.topPrescSNCF = this.dialogdata.factureFR.prescription.topPrescSNCF === '' ? '0' : this.dialogdata.factureFR.prescription.topPrescSNCF;
    this.prescription.originePrescription = this.dialogdata.factureFR.prescription.originePrescription;

    // Initialize with existing prescription date, invoice date (today), or formatted today
    const defaultDate = this.dialogdata.factureFR.prescription.datePresc || this.dialogdata.factureFR.dateFacture || new Intl.DateTimeFormat('fr-FR').format(new Date());
    this.prescription.datePresc = defaultDate;
  }

  /**
   * Focuses and selects the datePresc input on dialog open.
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.datePrescInput?.nativeElement && !this.dialogdata.isDisabled) {
        this.datePrescInput.nativeElement.focus();
        this.datePrescInput.nativeElement.select();
      }
    }, 0);
  }

  /**
   * Sets prescription date to today in FR format (DD/MM/YYYY).
   */
  public setToday(): void {
    this.prescription.datePresc = new Intl.DateTimeFormat('fr-FR').format(new Date());
  }

  /**
   * Normalizes and auto-completes shorthand date inputs on blur.
   * Handles formats: D, DD -> DD/MM/YYYY; DDMM -> DD/MM/YYYY; DDMMYY -> DD/MM/20YY; DDMMYYYY -> DD/MM/YYYY; DD/MM -> DD/MM/YYYY
   */
  public onBlurDatePresc(): void {
    if (!this.prescription.datePresc) return;

    let val = this.prescription.datePresc.trim().replace(/[-.]/g, '/');
    const today = new Date();
    const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
    const currentYear = today.getFullYear().toString();

    // 1 or 2 digits: ex "5" or "15" -> day of current month & year
    if (/^\d{1,2}$/.test(val)) {
      val = `${val.padStart(2, '0')}/${currentMonth}/${currentYear}`;
    }
    // 4 digits consecutive: ex "1508" -> day + month of current year
    else if (/^\d{4}$/.test(val)) {
      val = `${val.substring(0, 2)}/${val.substring(2, 4)}/${currentYear}`;
    }
    // 6 digits: ex "150826" -> DD/MM/20YY
    else if (/^\d{6}$/.test(val)) {
      val = `${val.substring(0, 2)}/${val.substring(2, 4)}/20${val.substring(4, 6)}`;
    }
    // 8 digits consecutive: ex "15082026"
    else if (/^\d{8}$/.test(val)) {
      val = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4, 8)}`;
    }
    // Shorthand "15/8" or "15/08" -> DD/MM/YYYY (current year)
    else if (/^\d{1,2}\/\d{1,2}$/.test(val)) {
      const parts = val.split('/');
      val = `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${currentYear}`;
    }

    this.prescription.datePresc = val;
  }

  /**
   * Closes the dialog, returning the updated prescription on save.
   * @param save True to save and return prescription data, false to cancel
   */
  public onClose(save: boolean): void {
    if (save) {
      this.onBlurDatePresc();
      this.dialogRef.close(this.prescription);
    } else {
      this.dialogRef.close(undefined);
    }
  }
}
